import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { InfoSignIcon, Pane } from "evergreen-ui";
import {
    AbstractTableViewProvider
} from "../../common/tables/view/AbstractTableViewProvider";
import { TableView } from "../../common/tables/view/TableView";
import { PLPRTableRow } from "../../../../common/tables/product-listing/payout-rule-mapping/PLPRTableRow";
import { ProductListingToPayoutRule, PayoutRule, CostPayoutModifier } from "../../../../common/models";
import { PLPRCreateInput, PLPRUpdateInput } from "../../../../common/inputs/ProductListingToPayoutRuleInput";
import { QueryInput } from "../../../../common/query-filters";
import {
    queryPayoutRuleMappingsByProductListing,
    useCreatePayoutRuleMappingMutation,
    useUpdatePayoutRuleMappingMutation,
} from "../../datastore/ProductListingToPayoutRuleDataStore";
import { queryPayoutRulesByStore } from "../../datastore/PayoutRuleDataStore";
import { queryAllCostPayoutModifiers } from "../../datastore/CostPayoutModifierDataStore";
import { DefaultPLPRTableConfig } from "./payout-rule-mapping/tables/config/DefaultPLPRTableConfig";
import { PLPRTableCellFactory } from "./payout-rule-mapping/factory/PLPRTableCellFactory";
import { PLPRTableRowSideSheetForm } from "./payout-rule-mapping/tables/PLPRTableRowSideSheetForm";
import { TableCellFactory } from "../../common/tables/factory";
import { EmptyState } from "../../common/EmptyState";
import { RampartAuthorized } from "../../rampart/RampartAuthorized";

export const PayoutRuleMappingTab: FC<{ productListingId: string; storeId?: string }> = ({ productListingId, storeId }) => {
    // Payout rules belong to a store, so a product listing without a store cannot have mappings
    if (!storeId) {
        return (
            <Pane padding={24}>
                <EmptyState
                    title="No store linked"
                    subtitle="This product listing is not linked to a store"
                >
                    <InfoSignIcon size={60} />
                </EmptyState>
            </Pane>
        );
    }

    return (
        <RampartAuthorized requiredGroups={["juniper.self-serve.admin"]}>
            <PayoutRuleMappingTableContent productListingId={productListingId} storeId={storeId} />
        </RampartAuthorized>
    );
};

const PayoutRuleMappingTableContent: FC<{ productListingId: string; storeId: string }> = ({ productListingId, storeId }) => {
    const defaultCreateInput: Partial<PLPRCreateInput> = useMemo(() => {
        return {
            productListingId,
        };
    }, [productListingId]);

    const [showCreateRowForm, setShowCreateRowForm] = useState<boolean>(false);
    const [createInput, setCreateInput] = useState<Partial<PLPRCreateInput>>(defaultCreateInput);
    const [payoutRules, setPayoutRules] = useState<PayoutRule[]>([]);
    const [costPayoutModifiers, setCostPayoutModifiers] = useState<CostPayoutModifier[]>([]);

    // Load payout rules for this store and CPMs when create side sheet opens
    useEffect(() => {
        if (showCreateRowForm) {
            queryPayoutRulesByStore(storeId)
                .then(setPayoutRules)
                .catch(() => setPayoutRules([]));
            queryAllCostPayoutModifiers()
                .then(setCostPayoutModifiers)
                .catch(() => setCostPayoutModifiers([]));
        }
    }, [showCreateRowForm, storeId]);

    const plprTableCellFactory: TableCellFactory<PLPRTableRow, ProductListingToPayoutRule> = useMemo(() => {
        return new PLPRTableCellFactory();
    }, []);

    const queryTableData = useCallback((input: QueryInput<PLPRTableRow>) => {
        return queryPayoutRuleMappingsByProductListing({
            productListingId,
            options: {
                take: input.pageSize,
            },
        });
    }, [productListingId]);

    const [
        createPayoutRuleMapping,
        {
            loading: createLoading,
            error: createError,
            called: createCalled,
        }
    ] = useCreatePayoutRuleMappingMutation();

    const [
        updatePayoutRuleMapping,
        {
            loading: updateLoading,
            called: updateCalled,
            error: updateError,
        }
    ] = useUpdatePayoutRuleMappingMutation();

    const onUpdateRow = useCallback((
        plprList: ProductListingToPayoutRule[],
        id: string | number,
        columnName: keyof PLPRTableRow,
        value: string | number | boolean | null,
        _label?: string,
    ): ProductListingToPayoutRule[] => {
        return plprList.map((plpr: ProductListingToPayoutRule) => {
            if (plpr.id !== id) {
                return plpr;
            }

            updatePayoutRuleMapping({ id: plpr.id, [columnName]: value } as PLPRUpdateInput);

            return { ...plpr, [columnName]: value };
        });
    }, [updatePayoutRuleMapping]);

    const onOpenSideSheet = useCallback(() => {
        setShowCreateRowForm(true);
    }, []);

    const onSubmitSideSheet = useCallback(() => {
        if (!createInput.payoutRuleId || !createInput.startDate || createInput.startThreshold === undefined) {
            return;
        }
        createPayoutRuleMapping(createInput as PLPRCreateInput);
    }, [createPayoutRuleMapping, createInput]);

    const onCancelSideSheet = useCallback(() => {
        setShowCreateRowForm(false);
    }, []);

    // Reset form state after successful creation
    useEffect(() => {
        if (!createCalled) {
            return;
        }

        if (!createLoading && !createError) {
            setCreateInput(defaultCreateInput);
            setShowCreateRowForm(false);
        }
    }, [createLoading, createCalled, createError, defaultCreateInput]);

    const emptyViewProps = useMemo(() => {
        return {
            primaryText: "No payout rules assigned",
            secondaryText: "Assign a payout rule to get started",
            onClickSecondaryText: onOpenSideSheet,
        };
    }, [onOpenSideSheet]);

    const errorViewProps = useMemo(() => {
        return {
            primaryText: "Failed to load payout rule mappings",
            secondaryText: "Refresh the page to try again",
        };
    }, []);

    return (
        <>
            <AbstractTableViewProvider
                queryTableData={queryTableData}
                onUpdateRow={onUpdateRow}
                updateRowLoading={updateLoading}
                updateRowCalled={updateCalled}
                updateRowError={updateError}
                createRowCalled={createCalled}
                createRowError={createError}
                createRowLoading={createLoading}
                tableCellFactory={plprTableCellFactory}
                savedTableConfigProps={{
                    tableName: "plprMapping",
                    identifiers: ["v1"],
                    defaultConfigValue: DefaultPLPRTableConfig,
                }}
            >
                <TableView
                    errorViewProps={errorViewProps}
                    emptyViewProps={emptyViewProps}
                />
            </AbstractTableViewProvider>
            <PLPRTableRowSideSheetForm
                isShown={showCreateRowForm}
                plprInput={createInput}
                payoutRules={payoutRules}
                costPayoutModifiers={costPayoutModifiers}
                mutationLoading={createLoading}
                mutationCalled={createCalled}
                mutationError={createError}
                onDismiss={onCancelSideSheet}
                onOpen={onOpenSideSheet}
                onSubmit={onSubmitSideSheet}
                onInputChanged={setCreateInput}
            />
        </>
    );
};
